import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Single source of truth — everything is treated as Toronto local time.
// We do NOT convert from UTC. Stored datetimes are rendered as-is in Toronto.
dayjs.extend(utc);
dayjs.extend(timezone);

export const APP_TIMEZONE = "America/Toronto";
dayjs.tz.setDefault(APP_TIMEZONE);

/**
 * Parse a stored datetime as Toronto local time.
 * Strips any trailing Z or +/-HH:MM offset so the value is treated literally
 * (no UTC→local conversion). e.g. "2026-04-29T13:00:00.000Z" → 1:00 PM.
 */
export const toAppTz = (value: any) => {
  if (value === null || value === undefined || value === "") return null;
  let v: any = value;
  if (typeof v === "string") {
    // Drop trailing "Z" and any explicit "+HH:MM" / "-HH:MM" offset
    v = v.replace(/\.\d+Z$/i, "").replace(/Z$/i, "").replace(/[+-]\d{2}:?\d{2}$/, "");
  }
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

/**
 * Format a stored datetime in Toronto time (no UTC conversion).
 * Default format: "DD-MMM-YY h:mm A". Returns "" when input is invalid.
 */
export const formatDateTime = (
  value: any,
  format: string = "DD-MMM-YY h:mm A"
): string => {
  const d = toAppTz(value);
  return d ? d.format(format) : "";
};

export const formatTime = (value: any): string =>
  formatDateTime(value, "h:mm A");

export const formatDate = (value: any): string =>
  formatDateTime(value, "DD-MMM-YY");

/**
 * Serialize a value for storage in Toronto local time WITHOUT a Z/offset suffix.
 * Use this in submit payloads so the backend persists the value as the literal
 * Toronto wall-clock time (e.g. "2026-04-29 09:00:00") instead of a UTC ISO.
 */
export const toSaveString = (value: any): string | null => {
  const d = toAppTz(value);
  return d ? d.format("YYYY-MM-DD HH:mm:ss") : null;
};

/**
 * Walk an object/array and convert any datetime field (those listed in
 * DEFAULT_DATETIME_FIELDS or a custom list) to a Toronto-local non-UTC string.
 * Use before sending payloads so nothing leaves the client with "Z".
 */
export const DEFAULT_DATETIME_FIELDS = [
  "dateTime",
  "departureDateTime",
  "destinationDateTime",
  "returnDateTime",
  "pickupDateTime",
  "issueDate",
  "dueDate",
  "paymentDate",
];

export const normalizeDateTimeFields = <T = any>(
  payload: T,
  fields: string[] = DEFAULT_DATETIME_FIELDS
): T => {
  if (payload === null || payload === undefined) return payload;
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeDateTimeFields(item, fields)) as any;
  }
  if (typeof payload === "object") {
    // Pass through dayjs / Date instances as raw values for the caller to format
    if ((payload as any)._isAMomentObject || (payload as any).$isDayjsObject) return payload;
    const out: any = {};
    for (const key of Object.keys(payload as any)) {
      const val = (payload as any)[key];
      if (fields.includes(key) && val !== null && val !== undefined && val !== "") {
        out[key] = toSaveString(val);
      } else if (typeof val === "object" && val !== null) {
        out[key] = normalizeDateTimeFields(val, fields);
      } else {
        out[key] = val;
      }
    }
    return out;
  }
  return payload;
};
